import styled from '@emotion/styled';
import { ArrowOutwardRounded } from '@mui/icons-material';
import { useMemo, useState } from 'react';
import ThemeToggle from '~/modules/Elements/ThemeToggle';
import { useDocumentTitle } from '~/modules/hooks/useDocumentTitle';
import LiveTvChannelCard from '../components/LiveTvChannelCard';
import LiveTvFilters from '../components/LiveTvFilters';
import { useLiveTvChannels } from '../hooks/useLiveTvChannels';
import LiveTvLayout from '../layout/LiveTvLayout';

const KARAOKE_URL = 'https://fastkaraoke.com';

export default function LiveTvHomePage() {
  useDocumentTitle('fastLiveTv');

  const { channels, loading, error } = useLiveTvChannels();

  const [search, setSearch] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');

  const countries = useMemo(() => {
    return [...new Set(channels.map((channel) => channel.country).filter(Boolean) as string[])].sort();
  }, [channels]);

  const filteredChannels = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return channels.filter((channel) => {
      const matchesSearch =
        !normalizedSearch ||
        channel.name.toLowerCase().includes(normalizedSearch) ||
        channel.country?.toLowerCase().includes(normalizedSearch) ||
        channel.category?.toLowerCase().includes(normalizedSearch) ||
        channel.source?.toLowerCase().includes(normalizedSearch);

      const matchesCountry = !selectedCountry || channel.country === selectedCountry;

      return matchesSearch && matchesCountry;
    });
  }, [channels, search, selectedCountry]);

  return (
    <LiveTvLayout>
      <CatalogHeader>
        <TitleGroup>
          <TitleIcon src="/live-tv/logo.png" alt="" aria-hidden="true" />
          <div>
            <Title>fastLiveTv</Title>
            <ChannelCount>{filteredChannels.length} channels</ChannelCount>
          </div>
        </TitleGroup>
        <HeaderActions>
          <KaraokeLink href={KARAOKE_URL} aria-label="Abrir Karaoke en fastkaraoke.com">
            <KaraokeBrandIcon src="/logo.svg" alt="" aria-hidden="true" />
            <span>Ir a Karaoke</span>
            <ArrowOutwardRounded aria-hidden="true" />
          </KaraokeLink>
          <ThemeToggle />
        </HeaderActions>
      </CatalogHeader>

      <LiveTvFilters
        search={search}
        selectedCountry={selectedCountry}
        countries={countries}
        onSearchChange={setSearch}
        onCountryChange={setSelectedCountry}
      />

      {loading && <StateMessage>Loading channels...</StateMessage>}
      {error && <StateMessage data-error>Error loading channels: {error}</StateMessage>}

      {!loading && !error && (
        <ChannelGrid>
          {filteredChannels.map((channel) => (
            <LiveTvChannelCard key={channel.id} channel={channel} />
          ))}
        </ChannelGrid>
      )}
    </LiveTvLayout>
  );
}

const CatalogHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 620px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const TitleIcon = styled.img`
  width: 46px;
  height: 46px;
  object-fit: cover;
  object-position: center;
  border-radius: 8px;
  border: 1px solid var(--walky-border);
  box-shadow: 0 5px 14px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  margin: 0;
  color: var(--walky-ink);
  font-size: clamp(24px, 3vw, 34px);
  line-height: 1;
  letter-spacing: 0;
`;

const ChannelCount = styled.div`
  margin-top: 5px;
  color: var(--walky-muted);
  font-size: 14px;
  font-weight: 600;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;

  @media (max-width: 620px) {
    width: 100%;
  }
`;

const KaraokeLink = styled.a`
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 16px;
  border: 1px solid rgba(244, 90, 10, 0.45);
  border-radius: 8px;
  background: linear-gradient(135deg, var(--walky-orange), #ff7a1a);
  color: #ffffff;
  box-shadow:
    0 12px 24px rgba(244, 90, 10, 0.18),
    inset 0 1px 0 rgba(255, 255, 255, 0.24);
  cursor: pointer;
  font-size: 14px;
  font-weight: 800;
  line-height: 1;
  text-decoration: none;
  transition:
    border-color 160ms ease,
    box-shadow 160ms ease,
    filter 160ms ease,
    transform 160ms ease;

  &:hover,
  &:focus-visible {
    border-color: var(--walky-orange);
    filter: saturate(1.06) brightness(1.02);
    box-shadow:
      0 16px 30px rgba(244, 90, 10, 0.24),
      inset 0 1px 0 rgba(255, 255, 255, 0.28);
    transform: translateY(-1px);
    outline: none;
  }

  svg {
    width: 18px;
    height: 18px;
  }

  @media (max-width: 620px) {
    flex: 1;
  }
`;

const KaraokeBrandIcon = styled.img`
  width: 22px;
  height: 22px;
  display: block;
  object-fit: contain;
  border-radius: 6px;
  background: #ffffff;
  padding: 2px;
  box-sizing: border-box;
  box-shadow: inset 0 0 0 1px rgba(24, 24, 27, 0.08);
`;

const ChannelGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;

  @media (max-width: 420px) {
    grid-template-columns: 1fr;
  }
`;

const StateMessage = styled.div`
  padding: 18px;
  border-radius: 8px;
  background: var(--walky-surface);
  color: var(--walky-muted);
  border: 1px solid var(--walky-border);

  &[data-error='true'] {
    color: var(--walky-error-text);
    border-color: var(--walky-error-border);
    background: var(--walky-error-bg);
  }
`;
